import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  EmpQualificationVm,
  EmpQualificationListItem,
  EmpQualificationListResponse,
  QualificationTypeOption,
} from '@/api/types/empQualification.types'

export class EmpQualificationService {
  /** GET /api/EmpQualification/GetQualificationTypes — returns qualification types for dropdown */
  async getQualificationTypes(): Promise<QualificationTypeOption[]> {
    const url = endpoints.empQualification.getQualificationTypes
    const res = await apiClient.get<QualificationTypeOption[] | { data: QualificationTypeOption[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as QualificationTypeOption[]
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data as QualificationTypeOption[]
    return []
  }

  /** GET /api/EmpQualification/GetAll?branchId= — returns qualifications for branch */
  async getAll(branchId: number, pageNumber = 1, pageSize = 10000): Promise<EmpQualificationListResponse> {
    const url = endpoints.empQualification.getAll(branchId, pageNumber, pageSize)
    const res = await apiClient.get<EmpQualificationListResponse | EmpQualificationListItem[] | { data: EmpQualificationListItem[] }>(url)
    const raw = res.data as any

    const extractArray = (value: any): EmpQualificationListItem[] | undefined => {
      if (Array.isArray(value)) return value as EmpQualificationListItem[]
      return undefined
    }

    let data: EmpQualificationListItem[] | undefined
    if (raw && typeof raw === 'object') {
      // Common shapes:
      // 1) { data: [...] }
      // 2) { data: { data: [...] } }
      // 3) { data: { items: [...] } }
      // 4) { items: [...] }
      data =
        extractArray(raw.data) ??
        extractArray(raw.data?.data) ??
        extractArray(raw.data?.items) ??
        extractArray(raw.data?.result) ??
        extractArray(raw.items) ??
        extractArray(raw.result)
    } else {
      data = extractArray(raw)
    }

    if (data) {
      return {
        data,
        totalRecords:
          raw?.totalRecords ??
          raw?.totalCount ??
          raw?.data?.totalRecords ??
          raw?.data?.totalCount ??
          data.length ??
          0,
        pageNumber: raw?.pageNumber ?? raw?.data?.pageNumber ?? pageNumber,
        pageSize: raw?.pageSize ?? raw?.data?.pageSize ?? data.length,
      }
    }

    return { data: [], totalRecords: 0, pageNumber: 1, pageSize: 0 }
  }

  async getById(id: number): Promise<EmpQualificationVm> {
    const url = endpoints.empQualification.getById(id)
    const res = await apiClient.get<EmpQualificationVm | { data: EmpQualificationVm }>(url)
    const raw = res.data as any
    if (raw && typeof raw === 'object' && 'data' in raw) return raw.data as EmpQualificationVm
    return raw as EmpQualificationVm
  }

  async getByEmpId(empId: number): Promise<EmpQualificationListItem[]> {
    const url = endpoints.empQualification.getByEmpId(empId)
    const res = await apiClient.get<EmpQualificationListItem[] | { data: EmpQualificationListItem[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as EmpQualificationListItem[]
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray(raw.data)) {
      return raw.data as EmpQualificationListItem[]
    }
    return []
  }

  async create(payload: EmpQualificationVm): Promise<unknown> {
    const url = endpoints.empQualification.create
    const res = await apiClient.post(url, payload)
    return res.data
  }

  async update(payload: EmpQualificationVm): Promise<unknown> {
    const url = endpoints.empQualification.update
    const res = await apiClient.put(url, payload)
    return res.data
  }

  async delete(id: number): Promise<void> {
    const url = endpoints.empQualification.delete(id)
    await apiClient.delete(url)
  }
}

export const empQualificationService = new EmpQualificationService()
